import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { Stack, useToast } from '@kinesis/bungle';
import { useDisclosureId } from '@/hooks/use-disclosure-id';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { useIsEditor } from '@/hooks/use-is-editor';
import { useFlag } from '@/hooks/use-flag';
import {
  useDownloadEvidenceMutation,
  useGetDisclosureQuery,
  useGetDisclosurePageQuery,
  useGetDisclosureSummaryQuery,
  useUpdateDisclosureMutation,
} from '@/api/disclosures';
import { useGetPlacesQuery } from '@/api/places';
import {
  useDownloadScenarioMethodologyMutation,
  useGetScenariosQuery,
} from '@/api/scenarios';
import { Loading } from '@/components/loading';
import { Layout } from '@/components/layout';
import { DisclosureHeader } from '@/components/disclosure-header';
import { DisclosureSidebar } from '@/components/disclosure-sidebar';
import { DisclosureSaveBar } from '@/components/disclosure-save-bar';
import { DisclosureOverview } from '@/components/disclosure-overview';
import { DisclosureLocation } from '@/components/disclosure-location';
import { DisclosureMethodology } from '@/components/disclosure-methodology';
import { DisclosureSource } from '@/components/disclosure-source';
import { DisclosureSectors } from '@/components/disclosure-sectors';
import { DisclosureSector } from '@/components/disclosure-sector';
import { DisclosureGpcSummary } from '@/components/disclosure-gpc-summary';
import { DisclosureGpcDetail } from '@/components/disclosure-gpc-detail';
import { DisclosureNotConfigured } from '@/components/disclosure-not-configured';
import { DisclosureNotFound } from '@/components/disclosure-not-found';
import { parsePageKey, sectorPageKey, sourcePageKey } from '@/data/pages';
import { disclosurePagePath } from '@/links';
import {
  CommunitySubSector,
  Disclosure as DisclosureType,
  DisclosurePageKey,
  DisclosurePageOutput,
  SourceType,
} from '@/types';
import {
  DisclosureContainer,
  FullWidthSection,
  SidebarCell,
  BodyCell,
  FooterCell,
} from './disclosure.styles';

// Open-disclosure page. Same structure as suit's disclosure page: the
// shared page/group framework rendered in the disclosure grid — a
// full-width editable header, a sidebar listing the pages `/summary`
// returns (grouped by group), the selected page's body, and a sticky
// footer save bar. The selected page is driven by a `?page=` query param
// (default `overview`); only what the API returns is rendered.
//
// Owns the local edit state for name + period: `localDisclosure` overlays
// the server copy while edits are pending; `hasChanges` gates the save
// bar. Discard and save-success both clear `localDisclosure`, falling
// back to the re-fetched server copy via RTK tag invalidation.

const PageContent = (props: PageContentProps) => {
  const {
    pageData,
    onSelectSource,
    onSelectSector,
    onDownloadEvidence,
    onDownloadMethodology,
    downloadingEvidenceId,
    downloadingMethodology,
    showActivityBreakdown,
  } = props;
  switch (pageData.type) {
    case 'overview':
      return (
        <DisclosureOverview
          pageData={pageData}
          onSelectSource={onSelectSource}
          showActivityBreakdown={showActivityBreakdown}
        />
      );
    case 'location':
      return <DisclosureLocation pageData={pageData} />;
    case 'methodology':
      return (
        <DisclosureMethodology
          pageData={pageData}
          onDownloadEvidence={onDownloadEvidence}
          onDownloadMethodology={onDownloadMethodology}
          downloadingEvidenceId={downloadingEvidenceId}
          downloadingMethodology={downloadingMethodology}
        />
      );
    case 'source':
      return <DisclosureSource pageData={pageData} />;
    case 'sectors':
      return (
        <DisclosureSectors
          pageData={pageData}
          onSelectSector={onSelectSector}
          showActivityBreakdown={showActivityBreakdown}
        />
      );
    case 'sector':
      return <DisclosureSector pageData={pageData} />;
    case 'gpc-summary':
      return <DisclosureGpcSummary pageData={pageData} />;
    case 'gpc-detail':
      return <DisclosureGpcDetail pageData={pageData} />;
    case 'not-configured':
      return <DisclosureNotConfigured />;
    case 'not-found':
      return <DisclosureNotFound />;
  }
};

type PageContentProps = {
  pageData: DisclosurePageOutput;
  onSelectSource: (sourceType: SourceType) => void;
  onSelectSector: (subSector: CommunitySubSector) => void;
  onDownloadEvidence: (evidenceId: number) => void;
  onDownloadMethodology: (scenarioId: number) => void;
  downloadingEvidenceId?: number;
  downloadingMethodology?: boolean;
  showActivityBreakdown: boolean;
};

const Disclosure = () => {
  const inventoryId = useInventoryId();
  const disclosureId = useDisclosureId();
  const isEditor = useIsEditor();
  const toast = useToast('globalTop');
  const navigate = useNavigate();
  // Show the activity breakdown (one level under each source on the Overview
  // by-source table) behind its own flag, independent of the section gate.
  const showActivityBreakdown = useFlag('community.disclosure-depth');
  const [searchParams, setSearchParams] = useSearchParams();
  // The page selected via `?page=`, or `null` when absent/garbage; the
  // landing default is resolved below from the server's `firstPage`.
  const requestedPage = parsePageKey(searchParams.get('page'));
  // The configuration rows (period, …) collapse via a `?show=` URL param,
  // mirroring suit. Absent = shown; `?show=false` = collapsed. Toggling
  // preserves the other params (e.g. `?page=`).
  const showConfiguration = searchParams.get('show') !== 'false';
  const toggleConfiguration = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('show') === 'false') {
          next.delete('show');
        } else {
          next.set('show', 'false');
        }
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { data: serverDisclosure, isLoading: isLoadingDisclosure } =
    useGetDisclosureQuery(disclosureId);
  // The inventory's places back the header's place-scope picker. Fetched
  // here (the page owns data) and passed down; an inventory with no places
  // simply yields an empty list and the picker hides itself.
  const { data: places } = useGetPlacesQuery(inventoryId);
  // The inventory's scenarios back the header's scenario-selector row. Same
  // page-owns-data pattern; an inventory always has at least its auto-created
  // default scenario, so the row shows whenever the list loads.
  const { data: scenarios } = useGetScenariosQuery(inventoryId);

  const [updateDisclosure] = useUpdateDisclosureMutation();
  const [localDisclosure, setLocalDisclosure] = useState<
    DisclosureType | undefined
  >(undefined);
  const [hasChanges, setHasChanges] = useState(false);

  // The disclosure being viewed/edited: unsaved local edits overlay the
  // server copy. The summary and the page are computed against this
  // *effective* definition — POSTed to the simulate endpoints — so every
  // header edit (e.g. the period) recomputes the report live, before
  // Save. The definition is part of each query's arg (its cache key), so
  // editing it re-runs the query. Save persists; discard / save-success
  // clear the local copy and the refetched server copy takes over.
  // Mirrors suit's `disclosure.loading.tsx`.
  const effectiveDisclosure = localDisclosure ?? serverDisclosure;
  const { data: summary } = useGetDisclosureSummaryQuery(
    effectiveDisclosure
      ? {
          id: disclosureId,
          definition: effectiveDisclosure.definition,
          scenario: effectiveDisclosure.scenario ?? undefined,
        }
      : skipToken,
  );
  const summaryConfigured =
    summary?.status === 'configured' ? summary : undefined;
  // The landing page when `?page=` is absent: the server's `firstPage`, never
  // a hardcoded `overview` (a disclosure need not have one, and `firstPage` is
  // guaranteed to exist). Undefined while the summary loads or when the
  // disclosure isn't configured — the page query waits rather than guessing.
  const pageKey = requestedPage ?? summaryConfigured?.firstPage;
  const { data: pageData, isLoading: isLoadingPage } =
    useGetDisclosurePageQuery(
      effectiveDisclosure && pageKey
        ? {
            id: disclosureId,
            key: pageKey,
            definition: effectiveDisclosure.definition,
            scenario: effectiveDisclosure.scenario ?? undefined,
          }
        : skipToken,
    );

  const handleChange = useCallback((next: DisclosureType) => {
    setLocalDisclosure(next);
    setHasChanges(true);
  }, []);

  const handleDiscard = useCallback(() => {
    setLocalDisclosure(undefined);
    setHasChanges(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!localDisclosure) return;
    try {
      await updateDisclosure({
        id: localDisclosure.id,
        name: localDisclosure.name,
        definition: localDisclosure.definition,
        scenario: localDisclosure.scenario ?? undefined,
      }).unwrap();
      setLocalDisclosure(undefined);
      setHasChanges(false);
      toast('Disclosure saved.', { variant: 'success' });
    } catch {
      toast(`We couldn't save the disclosure. Please try again.`, {
        variant: 'error',
      });
    }
  }, [localDisclosure, updateDisclosure, toast]);

  const pageLink = useCallback(
    (key: DisclosurePageKey) =>
      disclosurePagePath(inventoryId, disclosureId, key),
    [inventoryId, disclosureId],
  );

  // Treemap deep-link: clicking a source box on the Overview navigates to that
  // source's page — the same `?page=` target the sidebar link uses, so pending
  // local edits (held in component state) survive the in-route navigation.
  const handleSelectSource = useCallback(
    (sourceType: SourceType) => navigate(pageLink(sourcePageKey(sourceType))),
    [navigate, pageLink],
  );

  // The by-sector treemap's counterpart: clicking a sub-sector box on the
  // "Emissions by sector" page deep-links to that sub-sector's page.
  const handleSelectSector = useCallback(
    (subSector: CommunitySubSector) =>
      navigate(pageLink(sectorPageKey(subSector))),
    [navigate, pageLink],
  );

  // Methodology evidence download: the signed URL is followed in the
  // mutation's `onQueryStarted` (the locked transfer-download pattern), so the
  // handler just fires it with the file's id + the inventory. The rows share
  // one mutation, so the in-flight row is tracked by id — its button spins.
  // The RTK promise settles either way (errors live on the result), so no
  // try/finally is needed to clear it.
  const [downloadEvidence] = useDownloadEvidenceMutation();
  const [downloadingEvidenceId, setDownloadingEvidenceId] = useState<
    number | undefined
  >(undefined);
  const handleDownloadEvidence = useCallback(
    async (evidenceId: number) => {
      setDownloadingEvidenceId(evidenceId);
      await downloadEvidence({ inventoryId, evidenceId });
      setDownloadingEvidenceId(undefined);
    },
    [downloadEvidence, inventoryId],
  );

  // Methodology-reference download: same locked pattern, keyed on the
  // EFFECTIVE scenario id the page's document carries (one scenario-keyed
  // route serves both the scenario page and this one). A single document
  // row, so the hook's own in-flight flag drives its spinner.
  const [downloadScenarioMethodology, { isLoading: isDownloadingMethodology }] =
    useDownloadScenarioMethodologyMutation();
  const handleDownloadMethodology = useCallback(
    (scenarioId: number) => {
      downloadScenarioMethodology({ scenarioId });
    },
    [downloadScenarioMethodology],
  );

  if (isLoadingDisclosure || !serverDisclosure) {
    return <Loading />;
  }

  const disclosure = localDisclosure ?? serverDisclosure;
  const pages = summaryConfigured?.pages ?? [];

  return (
    <DisclosureContainer>
      <FullWidthSection>
        <DisclosureHeader
          disclosure={disclosure}
          onChange={handleChange}
          scenarios={scenarios}
          places={places}
          showConfiguration={showConfiguration}
          toggleConfiguration={toggleConfiguration}
        />
      </FullWidthSection>
      <SidebarCell>
        <DisclosureSidebar
          pages={pages}
          pageKey={pageKey}
          pageLink={pageLink}
        />
      </SidebarCell>
      <BodyCell>
        <Layout>
          <Stack space='medium'>
            {summary?.status === 'not-configured' ? (
              // Not-configured is a property of the disclosure (the summary),
              // not a page — render it directly rather than relying on a page
              // key being sendable.
              <DisclosureNotConfigured />
            ) : isLoadingPage || !pageData ? (
              <Loading />
            ) : (
              <PageContent
                pageData={pageData}
                onSelectSource={handleSelectSource}
                onSelectSector={handleSelectSector}
                onDownloadEvidence={handleDownloadEvidence}
                onDownloadMethodology={handleDownloadMethodology}
                downloadingEvidenceId={downloadingEvidenceId}
                downloadingMethodology={isDownloadingMethodology}
                showActivityBreakdown={showActivityBreakdown}
              />
            )}
          </Stack>
        </Layout>
      </BodyCell>
      {hasChanges && (
        <FooterCell>
          <DisclosureSaveBar
            isEditor={isEditor}
            onSave={handleSave}
            onDiscard={handleDiscard}
          />
        </FooterCell>
      )}
    </DisclosureContainer>
  );
};

export { Disclosure };
